import React, { useState } from 'react';
import classes from './style.module.scss';
import Modal from '~/app/common/Modal';
import SelectedMaterialList from './SelectedMaterialList';
import { useStoreActions, useStoreState } from '~/store';
import Input from '~/app/modules/form/Search/Input';
import ManualPartForm from './ManualPartForm';
import Checkout from './Checkout';
import Icon from '~/app/common/Icon';
import useTranslation from '~/app/hooks/useTranslation';
import { Material } from '~/types';
import { DEPARTMENT_PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import useAccessControl from '~/app/hooks/useAccessControl';
import { IssueOrServiceType } from '~/app/data/statuses/chat';

export interface AddMaterialDialogProps {
  open: boolean
  onClose: () => void
  type: IssueOrServiceType
}

export type AddMaterialDialogomponent = React.FC<AddMaterialDialogProps>;

const AddMaterialDialog: AddMaterialDialogomponent = ({ open, onClose, type }) => {

  const basePath = (type === IssueOrServiceType.ISSUE )? 'issue' : 'service';
  const deptPrefix = useStoreState(state => state[basePath].data.departmentPrefix);
  const totalQuantity = useStoreState(state => state[basePath].tempMaterials.filter(dM => !dM.deleted)?.reduce((sum, part) => sum + part.quantity, 0));
  const materialUpdating = useStoreState(state => state[basePath].materialUpdating ?? []);
  const searchMaterialAction = useStoreActions(actions => actions[basePath].fetchSearchMaterials ?? []);

  const [isOpenCheckoutPage, setIsOpenCheckoutPage] = useState<boolean>(false);
  const [isOpenManualMaterialForm, setIsOpenManualMaterialForm] = useState<boolean>(false);
  const [searchText, setSearchText] = useState('');
  const [materialForEdit, setMaterialForEdit] = useState<Material>();

  const { t } = useTranslation('material');
  const { checkPermission } = useAccessControl();

  const addManualMaterialPermission = deptPrefix 
    ? checkPermission(DEPARTMENT_PERMISSION_GROUPS.addManualMaterial(deptPrefix))
    : false;

  const searchMaterial = (searchText: string) => {
    setSearchText(searchText);
    if (searchText) {
      searchMaterialAction({ searchText, offset: 0 });
    }
  }

  const closeAll = () => {
    setSearchText('');
    setIsOpenCheckoutPage(false);
    setIsOpenManualMaterialForm(false);
    onClose();
  }

  const editManualMaterial = (material: Material) => {
    setMaterialForEdit(material);
    setIsOpenManualMaterialForm(true);
  }

  return (
    <Modal className={classes['addMaterialDialog']} open={open} onClose={closeAll} position={'bottom'}>
      <div className={classes['partManager']}>
        <div className={classes['partsHeader']}>
          <div className={classes['partsSubHeader']}>
            <Icon name='arrow_back' onClick={closeAll}/>
            <span>{t('add')} {t('parts')}</span>
          </div>
          <button
            className={classes['saveButton']}
            onClick={() => setIsOpenCheckoutPage(true)}
            disabled={totalQuantity === 0}
            aria-disabled={totalQuantity === 0}
          >
            {t('save')} ({totalQuantity})
          </button>
        </div>
        <Input
          autoFocus={false}
          placeholder={t('search')}
          className={classes["searchInput"]}
          inputClassName={classes["input"]}
          onSearch={searchMaterial}
          fetching={materialUpdating}
          value={searchText}
          iconPosition='left'
        />
        <SelectedMaterialList searchText={searchText} editManualMaterial={editManualMaterial} type={type}/>
        {addManualMaterialPermission === true && (
          <button className={classes['addButton']} onClick={() => setIsOpenManualMaterialForm(true)}>
            {t('addPart')}
          </button>
        )}
      </div>
      <ManualPartForm open={isOpenManualMaterialForm} onClose={() => {setIsOpenManualMaterialForm(false); setMaterialForEdit(undefined);}} material={materialForEdit} type={type} />
      <Checkout open={isOpenCheckoutPage} onClose={() => setIsOpenCheckoutPage(false)} onCloseAll={closeAll} type={type} />
    </Modal>
  );
};

export default AddMaterialDialog;

